import { useState, useCallback, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import { Loader2, Crop as CropIcon, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

const ASPECTS = [
  { label: "1:1", value: 1 },
  { label: "4:3", value: 4 / 3 },
  { label: "3:4", value: 3 / 4 },
  { label: "16:9", value: 16 / 9 },
];

export default function CropModal({ image, onClose, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1);
  const [areaPct, setAreaPct] = useState(null);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) { event.preventDefault(); onClose(); }
      if (event.key !== "Tab") return;
      const controls = Array.from(dialogRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled])') || []).filter((el) => el.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const onComplete = useCallback((area) => setAreaPct(area), []);
  const src = fileUrl(image?.url || image?.image_path);

  const apply = async () => {
    const a = areaPct || { x: 0, y: 0, width: 100, height: 100 };
    setSaving(true);
    try {
      const { data } = await api.post(`/images/${image.id}/crop`, {
        x: Math.max(0, a.x / 100),
        y: Math.max(0, a.y / 100),
        w: Math.min(1, a.width / 100),
        h: Math.min(1, a.height / 100),
      });
      toast.success("Bild zugeschnitten.");
      onCropped?.(data);
      onClose();
    } catch {
      toast.error("Zuschneiden fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="crop-title" className="dark fixed inset-0 z-[10000] flex flex-col bg-fundio-dark" data-testid="crop-modal">
      <div className="flex items-center justify-between border-b border-fundio-line px-4 py-3">
        <span id="crop-title" className="flex items-center gap-2 font-heading text-base font-bold text-fundio-cream">
          <CropIcon size={18} className="text-fundio-gold" /> Bild zuschneiden
        </span>
        <button ref={closeRef} type="button" aria-label="Zuschneiden schliessen" disabled={saving} data-testid="crop-close" onClick={onClose} className="flex h-12 w-12 items-center justify-center rounded-md border border-fundio-line text-fundio-cream hover:bg-fundio-surface">
          <X size={18} />
        </button>
      </div>

      <div className="relative flex-1 bg-black">
        {src && (
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            restrictPosition
          />
        )}
      </div>

      <div className="space-y-3 border-t border-fundio-line bg-fundio-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-fundio-cream/50">Format</span>
          {ASPECTS.map((a) => (
            <button type="button" disabled={saving} key={a.label} data-testid={`crop-aspect-${a.label}`} onClick={() => setAspect(a.value)}
              className={`min-h-12 rounded-md px-3 py-1.5 text-sm font-medium ${aspect === a.value ? "bg-fundio-gold text-fundio-dark" : "border border-fundio-line text-fundio-cream hover:text-fundio-cream"}`}>
              {a.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wide text-fundio-cream/50">Zoom</span>
          <input aria-label="Zoom" disabled={saving} type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="min-h-12 flex-1 accent-fundio-gold" />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" disabled={saving} onClick={onClose} className="min-h-12 rounded-lg border border-fundio-line px-4 py-2.5 text-sm font-medium text-fundio-cream hover:bg-fundio-dark">Abbrechen</button>
          <button type="button" data-testid="crop-apply" onClick={apply} disabled={saving}
            className="flex min-h-12 items-center gap-2 rounded-lg bg-fundio-gold px-4 py-2.5 text-sm font-medium text-fundio-dark hover:bg-fundio-gold-hover disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Zuschneiden
          </button>
        </div>
      </div>
    </div>
  );
}
